import { OpacityContainer } from "Hocs/withOpacity";
import { useTranslationContext } from "Hooks/useTranslationContext";
import { useSocketContext } from "./Common/Contexts";
import styled from "styled-components";
import Game from "Types/Game";
import Checkbox from "./Common/Checkbox";
import RadioButtons from "./Common/RadioButtons";
import { LargeHeading } from "./Common/Typography";
import { SmallFieldInput } from "./EditableField";
import TimeLimitInput from "./TimeLimitInput";
import { useEffect } from "react";
import { withPopup } from "./Common/Popup";
import Button from "./Common/Button";

const StyledCheckbox = styled(Checkbox)`
  padding-bottom: 0px !important;
  border: solid red !important;
`;

const StyledButton = styled(Button)`
  margin-top: 1em;
  font-size: 1em;
`;
const Centered = styled.div`
  text-align: center;
`;

const CenteringContainer = styled.div`
  grid-area: options;
  margin-bottom: 1em;
  margin-top: 1em;
  overflow: auto;
  font-size: 0.9em;
  width: 100%;
  display: flex;
  justify-content: center;
`;

const LeftAlignContainer = styled.div`
  max-width: 15em;
  text-align: left;
`;

interface GameSettingsProps {
  gameId: number;
  game: Game;
  showTimeLimitInputErrorMessage: boolean;
  setShowTimeLimitInputErrorMessage: (show: boolean) => void;
  onExit: () => void;
}

const GameSettings = ({
  gameId,
  game,
  showTimeLimitInputErrorMessage,
  setShowTimeLimitInputErrorMessage,
  onExit,
}: GameSettingsProps) => {
  const { t } = useTranslationContext();
  const socket = useSocketContext();

  const handleOnIncludeSwedishLettersChange = () =>
    socket.emit("changeIncludeSwedishLetters", gameId);
  const handleOnIncludeHardLettersChange = () =>
    socket.emit("changeIncludeHardLetters", gameId);
  const handleOnWinConditionChange = (winCondition: string) =>
    socket.emit("changeWinCondition", gameId, winCondition);
  const handleOnWinConditionRoundsChange = (rounds: number) =>
    socket.emit("changeWinConditionRounds", gameId, rounds);
  const handleOnWinConditionPointsChange = (points: number) =>
    socket.emit("changeWinConditionPoints", gameId, points);
  const handleOnTimeLimitEnabledChange = () =>
    socket.emit("changeTimeLimitEnabled", gameId);
  const handleOnDisableDone = () => socket.emit("changeDisableDone", gameId);
  const handleOnTimeLimitChange = (timeLimit: string) => {
    const timeLimitInt = parseInt(timeLimit);
    if (timeLimit === "") {
      socket.emit("changeTimeLimit", gameId, undefined);
    }
    if (timeLimitInt) {
      socket.emit("changeTimeLimit", gameId, (timeLimitInt + 3) * 1000);
    }
  };
  useEffect(() => {
    if (game.timeLimit !== null || !game.timeLimitEnabled) {
      setShowTimeLimitInputErrorMessage(false);
    } else {
      setShowTimeLimitInputErrorMessage(true);
    }
  }, [
    game.timeLimitEnabled,
    game.timeLimit,
    setShowTimeLimitInputErrorMessage,
  ]);
  return (
    <CenteringContainer>
      <LeftAlignContainer>
        <Centered>
          <LargeHeading>{`${t.options}`}</LargeHeading>
        </Centered>
        <Checkbox
          onCheckboxChange={handleOnIncludeSwedishLettersChange}
          isSelected={game.includeSwedishLetters}
          label={t["include.swedish.letters"]}
        />
        <Checkbox
          onCheckboxChange={handleOnIncludeHardLettersChange}
          isSelected={game.includeHardLetters}
          label={t["include.hard.letters"]}
        />
        <RadioButtons
          onRadioButtonChange={handleOnWinConditionChange}
          selectedValue={game.winCondition}
          label={t["win.conditions"]}
          options={[
            { label: t["none"], value: "none" },
            {
              label: t["number.of.rounds"],
              value: "rounds",
              child: (
                <>
                  <SmallFieldInput
                    type="text"
                    value={game.winConditionRounds}
                    onChange={(e) =>
                      handleOnWinConditionRoundsChange(
                        e.target.value === "" ? 0 : parseInt(e.target.value)
                      )
                    }
                    isEditing={true}
                    disabled={game.winCondition !== "rounds"}
                  />
                  {t["number.of.rounds"]}
                </>
              ),
            },
            {
              label: t["points"],
              value: "points",
              child: (
                <>
                  <SmallFieldInput
                    data-test="winConditionPoints"
                    type="text"
                    value={game.winConditionPoints}
                    onChange={(e) =>
                      handleOnWinConditionPointsChange(
                        e.target.value === "" ? 0 : parseInt(e.target.value)
                      )
                    }
                    isEditing={true}
                    disabled={game.winCondition !== "points"}
                  />
                  {t["points"]}
                </>
              ),
            },
          ]}
        />
        <StyledCheckbox
          onCheckboxChange={handleOnTimeLimitEnabledChange}
          isSelected={game.timeLimitEnabled}
          label={t["play.with.time.limit"]}
        />
        <OpacityContainer show={game.timeLimitEnabled} opacity={"0.3"}>
          <TimeLimitInput
            timeLimit={game.timeLimit}
            handleOnTimeLimitChange={handleOnTimeLimitChange}
            showErrorMessage={showTimeLimitInputErrorMessage}
            disabled={!game.timeLimitEnabled}
          />
          <Checkbox
            onCheckboxChange={handleOnDisableDone}
            isSelected={!game.disableDone}
            label={t["players.can.end.round"]}
            disabled={!game.timeLimitEnabled}
          />
        </OpacityContainer>
        <Centered>
          <StyledButton isSmall onClick={onExit}>
            {t.close}
          </StyledButton>
        </Centered>
      </LeftAlignContainer>
    </CenteringContainer>
  );
};

export default withPopup(GameSettings);
